/**
 * Document Controller
 *
 * Manages document capture flow including:
 * - Camera management for document scanning
 * - Front/back side capture
 * - Image preview and confirmation
 * - File upload support
 */

import {
  verificationState,
  DocumentType,
  DocumentSide,
  CameraFacingMode,
} from '../state/verification-state';
import { i18n } from '../../i18n';

// Re-export types for convenience
export type { DocumentType, DocumentSide, CameraFacingMode };

/**
 * Configuration for the document controller.
 */
export interface DocumentControllerConfig {
  /** Default camera facing mode. Default: 'environment' */
  defaultFacingMode: CameraFacingMode;
  /** JPEG quality for capture. Default: 0.9 */
  jpegQuality: number;
  /** Max file size for uploads in bytes. Default: 5MB */
  maxFileSizeBytes: number;
  /** Allowed file types. Default: ['image/jpeg', 'image/png', 'image/jpg'] */
  allowedFileTypes: string[];
  /** Ideal camera width. Default: 1280 */
  idealWidth: number;
  /** Ideal camera height. Default: 720 */
  idealHeight: number;
}

/**
 * Events emitted by the controller.
 */
export interface DocumentControllerEvents {
  onCameraStarted?: (side: DocumentSide) => void;
  onCameraStopped?: () => void;
  onCameraError?: (error: Error, message: string) => void;
  onCapture?: (side: DocumentSide, dataUrl: string) => void;
  onPreviewShow?: (side: DocumentSide, dataUrl: string) => void;
  onPreviewConfirm?: (side: DocumentSide) => void;
  onPreviewRetake?: (side: DocumentSide) => void;
  onDocumentComplete?: (frontImage: string, backImage: string | null) => void;
  onUIUpdate?: (sideText: string, instruction: string) => void;
}

/**
 * Default configuration values.
 */
const DEFAULT_CONFIG: DocumentControllerConfig = {
  defaultFacingMode: 'environment',
  jpegQuality: 0.9,
  maxFileSizeBytes: 5 * 1024 * 1024, // 5MB
  allowedFileTypes: ['image/jpeg', 'image/png', 'image/jpg'],
  idealWidth: 1280,
  idealHeight: 720,
};

/**
 * Maps browser error names to i18n error keys.
 */
const CAMERA_ERROR_I18N_MAP: Record<string, keyof typeof i18n extends never ? string : string> = {
  NotAllowedError: 'cameraPermissionDenied',
  PermissionDeniedError: 'cameraPermissionDenied',
  NotFoundError: 'cameraNotFound',
  DevicesNotFoundError: 'cameraNotFound',
  NotReadableError: 'cameraInUse',
  TrackStartError: 'cameraInUse',
  OverconstrainedError: 'cameraNotSupported',
  SecurityError: 'cameraSecurityError',
};

/**
 * Gets localized camera error message from browser error name.
 */
function getCameraErrorMessage(errorName: string): string {
  const translations = i18n.getTranslations();
  const i18nKey = CAMERA_ERROR_I18N_MAP[errorName];

  if (i18nKey) {
    const message = translations.errors[i18nKey as keyof typeof translations.errors];
    if (typeof message === 'string') {
      return message;
    }
  }

  // Fallback to generic error
  return translations.errors.unknownError;
}

/**
 * DocumentController manages document capture.
 *
 * Features:
 * - Front/back camera switching
 * - Progressive constraint fallback for device compatibility
 * - Image cropping based on visible area
 * - File upload alternative
 * - Preview and confirmation flow
 */
export class DocumentController {
  private config: DocumentControllerConfig;
  private events: DocumentControllerEvents;

  // Camera state
  private stream: MediaStream | null = null;
  private facingMode: CameraFacingMode;

  // Document state
  private documentType: DocumentType | null = null;
  private currentSide: DocumentSide = 'front';
  private frontImage: string | null = null;
  private backImage: string | null = null;

  // Element references (set by caller)
  private videoElement: HTMLVideoElement | null = null;
  private containerElement: HTMLElement | null = null;

  constructor(
    config: Partial<DocumentControllerConfig> = {},
    events: DocumentControllerEvents = {}
  ) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.events = events;
    this.facingMode = this.config.defaultFacingMode;
  }

  /**
   * Sets the video element reference.
   */
  setVideoElement(element: HTMLVideoElement): void {
    this.videoElement = element;
  }

  /**
   * Sets the container element reference (for cropping calculations).
   */
  setContainerElement(element: HTMLElement): void {
    this.containerElement = element;
  }

  /**
   * Sets the document type being captured.
   */
  setDocumentType(type: DocumentType): void {
    this.documentType = type;
    this.frontImage = null;
    this.backImage = null;
    this.currentSide = 'front';

    verificationState.update('document', {
      type,
      side: 'front',
      frontImage: null,
      backImage: null,
    });
  }

  /**
   * Gets the current document type.
   */
  getDocumentType(): DocumentType | null {
    return this.documentType;
  }

  /**
   * Starts the document camera.
   */
  async startCamera(): Promise<boolean> {
    console.log('[DocumentController] Starting camera for', this.currentSide, 'side...');
    console.log('[DocumentController] Facing mode:', this.facingMode);

    // Stop any existing stream
    this.stopCamera();

    if (!this.videoElement) {
      console.error('[DocumentController] Video element not set');
      return false;
    }

    try {
      // Progressive constraint fallback for device compatibility
      const constraintOptions: MediaStreamConstraints[] = [
        // First try: ideal constraints
        {
          video: {
            facingMode: { ideal: this.facingMode },
            width: { ideal: this.config.idealWidth },
            height: { ideal: this.config.idealHeight },
          },
        },
        // Fallback 1: exact facing mode without resolution constraints
        {
          video: {
            facingMode: this.facingMode,
          },
        },
        // Fallback 2: any camera
        {
          video: true,
        },
      ];

      let stream: MediaStream | null = null;
      let lastError: Error | null = null;

      for (const constraints of constraintOptions) {
        try {
          console.log('[DocumentController] Trying constraints:', JSON.stringify(constraints));
          stream = await navigator.mediaDevices.getUserMedia(constraints);
          console.log(
            '[DocumentController] Success with constraints:',
            JSON.stringify(constraints)
          );
          break;
        } catch (e) {
          const error = e instanceof Error ? e : new Error(String(e));
          console.warn('[DocumentController] Failed with constraints:', error.name, error.message);
          lastError = error;
        }
      }

      if (!stream) {
        throw lastError || new Error('No camera available');
      }

      this.stream = stream;
      this.videoElement.srcObject = stream;
      await this.videoElement.play();

      console.log('[DocumentController] Video playing:', !this.videoElement.paused);
      console.log(
        '[DocumentController] Video dimensions:',
        this.videoElement.videoWidth,
        'x',
        this.videoElement.videoHeight
      );

      // Update video mirror based on camera
      this.updateVideoMirror();

      // Update verification state
      verificationState.update('document', {
        cameraStream: stream,
        cameraFacingMode: this.facingMode,
      });

      this.events.onCameraStarted?.(this.currentSide);
      this.updateUI();

      return true;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('[DocumentController] Failed to start:', err.name, err.message);

      const errorMsg = getCameraErrorMessage(err.name);
      this.events.onCameraError?.(err, errorMsg);

      return false;
    }
  }

  /**
   * Stops the document camera.
   */
  stopCamera(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    verificationState.update('document', {
      cameraStream: null,
    });

    this.events.onCameraStopped?.();
  }

  /**
   * Switches between front and back camera.
   */
  async switchCamera(): Promise<boolean> {
    console.log('[DocumentController] Switching camera...');

    this.facingMode = this.facingMode === 'environment' ? 'user' : 'environment';
    console.log('[DocumentController] New facing mode:', this.facingMode);

    return this.startCamera();
  }

  /**
   * Updates video mirror based on facing mode.
   * Environment (back) camera should not mirror documents.
   */
  private updateVideoMirror(): void {
    if (!this.videoElement) return;

    if (this.facingMode === 'environment') {
      this.videoElement.style.transform = 'none';
    } else {
      // Mirror for user (front) camera
      this.videoElement.style.transform = 'scaleX(-1)';
    }
  }

  /**
   * Captures the current video frame.
   */
  capture(): string | null {
    console.log('[DocumentController] Capturing photo...');

    if (!this.videoElement || !this.videoElement.srcObject) {
      console.error('[DocumentController] No camera stream to capture from');
      return null;
    }

    // Get actual video dimensions
    const videoWidth = this.videoElement.videoWidth || this.config.idealWidth;
    const videoHeight = this.videoElement.videoHeight || this.config.idealHeight;

    // Calculate crop area based on container (object-cover behavior)
    const { cropX, cropY, cropWidth, cropHeight } = this.calculateCropArea(videoWidth, videoHeight);

    console.log('[DocumentController] Video:', videoWidth, 'x', videoHeight);
    console.log('[DocumentController] Crop area:', cropX, cropY, cropWidth, cropHeight);

    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = cropWidth;
    canvas.height = cropHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('[DocumentController] Cannot get canvas context');
      return null;
    }

    // If using front camera (mirrored), flip the capture
    if (this.facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }

    // Draw only the visible region
    ctx.drawImage(
      this.videoElement,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    );

    const dataUrl = canvas.toDataURL('image/jpeg', this.config.jpegQuality);

    // Store based on current side
    if (this.currentSide === 'front') {
      this.frontImage = dataUrl;
    } else {
      this.backImage = dataUrl;
    }

    // Update verification state
    verificationState.update('document', {
      [this.currentSide === 'front' ? 'frontImage' : 'backImage']: dataUrl,
    });

    console.log('[DocumentController] Photo captured for', this.currentSide, 'side');

    this.events.onCapture?.(this.currentSide, dataUrl);
    return dataUrl;
  }

  /**
   * Calculates crop area based on container dimensions (object-cover behavior).
   */
  private calculateCropArea(
    videoWidth: number,
    videoHeight: number
  ): { cropX: number; cropY: number; cropWidth: number; cropHeight: number } {
    if (!this.containerElement) {
      return { cropX: 0, cropY: 0, cropWidth: videoWidth, cropHeight: videoHeight };
    }

    const containerWidth = this.containerElement.clientWidth;
    const containerHeight = this.containerElement.clientHeight;

    const videoAspect = videoWidth / videoHeight;
    const containerAspect = containerWidth / containerHeight;

    let cropX = 0;
    let cropY = 0;
    let cropWidth = videoWidth;
    let cropHeight = videoHeight;

    if (videoAspect > containerAspect) {
      // Video is wider - crop left and right
      cropWidth = Math.floor(videoHeight * containerAspect);
      cropX = Math.floor((videoWidth - cropWidth) / 2);
    } else {
      // Video is taller - crop top and bottom
      cropHeight = Math.floor(videoWidth / containerAspect);
      cropY = Math.floor((videoHeight - cropHeight) / 2);
    }

    return { cropX, cropY, cropWidth, cropHeight };
  }

  /**
   * Handles file selection for document upload.
   */
  handleFileSelect(file: File): boolean {
    console.log('[DocumentController] File selected:', file.name, file.type, file.size);

    // Validate file type
    if (!this.config.allowedFileTypes.includes(file.type)) {
      console.error('[DocumentController] Invalid file type:', file.type);
      return false;
    }

    // Validate file size
    if (file.size > this.config.maxFileSizeBytes) {
      console.error('[DocumentController] File too large:', file.size);
      return false;
    }

    // Read file
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;

      if (this.currentSide === 'front') {
        this.frontImage = dataUrl;
      } else {
        this.backImage = dataUrl;
      }

      // Update verification state
      verificationState.update('document', {
        [this.currentSide === 'front' ? 'frontImage' : 'backImage']: dataUrl,
      });

      console.log('[DocumentController] File loaded for', this.currentSide, 'side');

      this.events.onCapture?.(this.currentSide, dataUrl);
      this.events.onPreviewShow?.(this.currentSide, dataUrl);
    };

    reader.onerror = () => {
      console.error('[DocumentController] Error reading file');
    };

    reader.readAsDataURL(file);
    return true;
  }

  /**
   * Shows preview for the current capture.
   */
  showPreview(): void {
    const image = this.currentSide === 'front' ? this.frontImage : this.backImage;
    if (image) {
      this.events.onPreviewShow?.(this.currentSide, image);
    }
  }

  /**
   * Confirms the current capture and advances to next side or completes.
   */
  confirmCapture(): void {
    console.log('[DocumentController] Confirming capture for', this.currentSide);

    this.events.onPreviewConfirm?.(this.currentSide);

    if (this.currentSide === 'front' && this.requiresBackSide()) {
      // Move to back side
      this.currentSide = 'back';
      verificationState.update('document', { side: 'back' });
      this.updateUI();
    } else {
      // Document complete
      if (this.frontImage) {
        this.events.onDocumentComplete?.(this.frontImage, this.backImage);
      }
    }
  }

  /**
   * Retakes the current capture.
   */
  retake(): void {
    console.log('[DocumentController] Retaking capture for', this.currentSide);

    if (this.currentSide === 'front') {
      this.frontImage = null;
    } else {
      this.backImage = null;
    }

    verificationState.update('document', {
      [this.currentSide === 'front' ? 'frontImage' : 'backImage']: null,
    });

    this.events.onPreviewRetake?.(this.currentSide);
  }

  /**
   * Checks if the document type requires a back side.
   */
  requiresBackSide(): boolean {
    // Passport doesn't require back side
    return this.documentType !== 'passport';
  }

  /**
   * Gets the current side being captured.
   */
  getCurrentSide(): DocumentSide {
    return this.currentSide;
  }

  /**
   * Gets the front image.
   */
  getFrontImage(): string | null {
    return this.frontImage;
  }

  /**
   * Gets the back image.
   */
  getBackImage(): string | null {
    return this.backImage;
  }

  /**
   * Checks if capture is complete.
   */
  isComplete(): boolean {
    if (!this.frontImage) return false;
    if (this.requiresBackSide() && !this.backImage) return false;
    return true;
  }

  /**
   * Updates the UI with current side info.
   */
  private updateUI(): void {
    const translations = i18n.getTranslations();
    const docCamera = translations.screens.documentCamera;

    const sideText = this.currentSide === 'front' ? docCamera.frontSide : docCamera.backSide;
    const instruction = this.currentSide === 'front' ? docCamera.scanFront : docCamera.scanBack;
    this.events.onUIUpdate?.(sideText, instruction);
  }

  /**
   * Resets the controller state.
   */
  reset(): void {
    this.stopCamera();
    this.documentType = null;
    this.currentSide = 'front';
    this.frontImage = null;
    this.backImage = null;
    this.facingMode = this.config.defaultFacingMode;

    verificationState.update('document', {
      type: null,
      side: 'front',
      frontImage: null,
      backImage: null,
      cameraStream: null,
      cameraFacingMode: this.config.defaultFacingMode,
    });
  }

  /**
   * Disposes of the controller.
   */
  dispose(): void {
    this.stopCamera();
    this.videoElement = null;
    this.containerElement = null;
  }
}

/**
 * Singleton instance for convenience.
 */
export const documentController = new DocumentController();
